import { useEffect, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useLogoutMutation } from "../../__generated__/graphql";

import type { TopBarProps, TopBarUser } from "../types/topbar.types";

type UseTopBarHookParams = Pick<TopBarProps, "onMenuToggle">;

const getStoredUser = (): TopBarUser | null => {
  const saved = localStorage.getItem("userData");

  if (!saved) return null;

  try {
    const parsed = JSON.parse(saved);

    if (!parsed?.profile) return null;

    return {
      firstName: parsed.profile.firstName ?? "",
      lastName: parsed.profile.lastName ?? "",
      roles: (parsed.email?.split("@")[0] ?? "").toUpperCase(),
    };
  } catch (error) {
    console.error("Failed to parse userData", error);
    return null;
  }
};

const getScreenFlags = () => {
  if (typeof window === "undefined") {
    return {
      isSmallMobile: false,
    };
  }

  return {
    isSmallMobile: window.innerWidth < 640, // sm
  };
};

export const useTopBarHook = ({ onMenuToggle }: UseTopBarHookParams) => {
  /* ========= Router / GraphQL ========= */
  const navigate = useNavigate();
  const [logoutMutation] = useLogoutMutation();

  /* ========= States ========= */
  const [user, setUser] = useState<TopBarUser | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  /* ========= Effects ========= */
  useEffect(() => {
    setUser(getStoredUser());
    setMounted(true);

    const { isSmallMobile } = getScreenFlags();
    setIsSmallMobile(isSmallMobile);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const { isSmallMobile } = getScreenFlags();
      setIsSmallMobile(isSmallMobile);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* ========= Derived ========= */
  const isProfileMenuOpen = Boolean(anchorEl);

  /* ========= Handlers ========= */
  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    if (!mounted) return;
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    if (!mounted) return;
    setAnchorEl(null);
  };

  const handleMenuToggle = () => {
    if (!mounted || !onMenuToggle) return;
    onMenuToggle();
  };

  const handleLogout = async () => {
    try {
      handleProfileMenuClose();

      try {
        (window as any).__INSPECTION_IS_EDITING = false;

        window.dispatchEvent(
          new CustomEvent("inspection:editing-changed", {
            detail: { isEditing: false },
          }),
        );
      } catch {
        //
      }

      await logoutMutation();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      const sidebarCollapsed = localStorage.getItem("sidebarCollapsed");

      localStorage.clear();

      if (sidebarCollapsed !== null) {
        localStorage.setItem("sidebarCollapsed", sidebarCollapsed);
      }

      navigate("/login", { replace: true });
    }
  };

  return {
    mounted,
    user,
    anchorEl,
    isProfileMenuOpen,
    isSmallMobile,
    handleProfileMenuOpen,
    handleProfileMenuClose,
    handleMenuToggle,
    handleLogout,
  };
};
